import type { SeatInfo, Snapshot } from "@app/protocol";
import { nonDefaultRules } from "@app/rules";
import { ruleLabelKey } from "../components/HouseRules";
import type { SeatLook } from "../components/Pod";
import type { useT } from "../i18n/context";
import { send } from "../ws/singleton";

export type T = ReturnType<typeof useT>["t"];

/** House rules that differ from the game's defaults, as short labels for the lobby summary. */
export function ruleLabels(t: T, settings: Snapshot["table"]["settings"]): string[] {
  const changed = nonDefaultRules(settings.variant, settings.rules);
  return Object.entries(changed).map(([key, value]) =>
    t(ruleLabelKey(settings.variant, key, value) as "home.goal"),
  );
}

export function targetLabel(t: T, settings: Snapshot["table"]["settings"]): string {
  if (settings.goal === null) return t("lobby.target.length", { length: settings.length });
  return settings.variant !== "batak"
    ? t("lobby.target.points", { goal: settings.goal })
    : t("lobby.target.goal", { goal: settings.goal });
}

/** Who is looking at the lobby. */
export interface Viewer {
  userId: string;
  seated: boolean;
  owner: boolean;
  ownerId: string | null;
}

/** The owner may remove a person from a seat, from their card; never themselves, never a bot (decision 99). */
function kickAction(seat: SeatInfo, viewer: Viewer, t: T): SeatLook["action"] {
  if (!viewer.owner || seat.userId === viewer.userId) return undefined;
  return {
    label: t("playerCard.kick"),
    onClick: () => send({ type: "kick", userId: seat.userId }),
  };
}

/**
 * A seat's pod in the lobby (design 3): an empty seat invites you to sit, the
 * owner wears the crown, bots and dropped players say so. Standing up is Back
 * (review 0012, decision 57).
 */
export function lobbySeatLook(
  seat: SeatInfo | null,
  index: number,
  viewer: Viewer,
  t: T,
): SeatLook {
  if (seat === null)
    return viewer.seated
      ? { status: t("lobby.empty"), tone: "dark" }
      : {
          status: t("lobby.seat.empty"),
          tone: "dark",
          onSit: () => send({ type: "sit", seat: index }),
        };
  if (seat.bot) return { status: t("lobby.seat.bot"), tone: "dark", badge: "bot" };
  if (!seat.connected)
    return {
      status: t("lobby.seat.away"),
      tone: "alert",
      badge: "away",
      action: kickAction(seat, viewer, t),
    };
  return {
    status: t("lobby.ready"),
    tone: "light",
    badge: seat.userId === viewer.ownerId ? "owner" : undefined,
    action: kickAction(seat, viewer, t),
  };
}
